import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CreativePreviews } from "reducers/CreativePreviews";
import SelectedRow from "utils/columns/SelectedRow";
import { DataGrid, ProgressIndicator } from "rhombus-react";
import {
	DEFAULT_ROW_HEIGHT,
	MIN_GRID_HEIGHT,
	PREVIEW_2_URL,
	TIA_DOMAIN_PUBLIC
} from "utils/constants";
import { getPRVW } from "utils/Flags";
import { isSocialCreative } from "utils/common";
import { isLegacyPreview } from "utils/Preview";
import { useGridScrollUtils } from "utils/DataGrid";
import { CREATIVE_PREVIEWS_COLUMNS } from "widgets/grid/columns/Previews";
import { useTooltipRebuild } from "hooks/useTooltipRebuild";
import Measure from "react-measure";

const mapDataToPreviews = (libraryId, previews = [], currentSelection) => {
	return previews.map((preview) => {
		// HOPI-1273 This temporary logic is to accommodate bridge in a transition period between new and Legacy creative preview UIs
		let {
				type,
				id,
				creatives = [],
				publicId,
			} = preview
			, publicUrlTiA = `${TIA_DOMAIN_PUBLIC}/p/${id}`
			, publicUrlVV = `${TIA_DOMAIN_PUBLIC}/pv/${id}`
			;

		if (type === "mockup"){
			publicUrlTiA = publicId ? `${TIA_DOMAIN_PUBLIC}/p/${publicId}` : "";
			publicUrlVV = "";
		} else {
			for (let i = 0; i < creatives.length; i++) {
				const creative = creatives[i];

				if(getPRVW()){
					publicUrlTiA = `${PREVIEW_2_URL}/p/${id}`;
					publicUrlVV = `${PREVIEW_2_URL}/pv/${id}`;
				} else {
					if(!isLegacyPreview(creative)){
						publicUrlTiA = `${PREVIEW_2_URL}/p/${id}`;
						if(isSocialCreative(creative)){
							publicUrlVV = "";
							break; // as soon as we find one social creative, we can break and use the overwritten links
						}
					}
				}	//end if(getPRVW())
			}	//end (for creatives.length)
		}	//end if(type = mockup)

		// end temp logic, but also remove the related { publicUrlTiA, publicUrlVV } from the Object.assign below.
		return Object.assign({
			libraryId,
		},
		preview,
		{
			publicUrlTiA,
			publicUrlVV,
		}, {
			isSelected: currentSelection.indexOf(preview.id) > -1,
		});
	});
};

export default function Grid({ libraryId, sort="", isLoading }) {
	const dispatch = useDispatch()
		, [sortColumn, sortDirection] = sort.split(":")
		, { previews, currentSelection } = useSelector((state) => {
			return {
				previews: state.creativePreviews.items,
				currentSelection: state.selected.creativePreviews,
			};
		})
		;

	const creativePreviews = useMemo(() => {
			return mapDataToPreviews(libraryId, previews, currentSelection);
		}, [libraryId, previews, currentSelection])
		, dataGridRef = useGridScrollUtils([creativePreviews])
		;

	const getRow = (rowID) => {
		if(rowID === -1){ return; } // -1 is the header row, and doesn't need to return data
		return creativePreviews[rowID];
	};

	useTooltipRebuild(creativePreviews);

	const dataGridProps = {
		columns: CREATIVE_PREVIEWS_COLUMNS,
		rowGetter: getRow,
		rowsCount: creativePreviews.length,
		rowRenderer: SelectedRow,
		rowHeight: DEFAULT_ROW_HEIGHT,
		sortColumn,
		sortDirection,
		onGridSort: (col, dir) => dispatch(CreativePreviews.setSort(col, dir)),
		enableCellAutoFocus: false, /*
			This is a ReactDataGrid setting that defaults to `true`, which seems to make it fight
			with anything else trying to utilize regular `tabindex` control flow, like blade forms.
		*/
	};

	return (
		<Measure>
			{(obj) => {
				let gridHeight = obj?.contentRect?.entry?.height || MIN_GRID_HEIGHT;
				return (
					<div className="measure-component" ref={obj.measureRef}>
						<ProgressIndicator isLoading={isLoading} />
						<div ref={dataGridRef}>
							<DataGrid
								{...dataGridProps}
								minHeight={gridHeight}
							/>
						</div>
					</div>
				);
			}}
		</Measure>
	);
}

Grid.displayName = "Grid";
